/**
 * Камера координатной плоскости.
 *
 * Главный инвариант: одна единица по `x` и одна по `y` занимают одинаковое
 * число пикселей. Поэтому масштаб один, а не два. Разные масштабы по осям
 * врут про наклон прямой и про форму параболы — то самое, чему лаборатория
 * должна научить.
 *
 * Модуль не знает ни о кривых, ни о React: на вход точки, на выход камера.
 */

export type PlotPoint = { x: number; y: number };
export type PlotSize = { width: number; height: number };

/** Центр кадра в мировых координатах и число пикселей в одной единице. */
export type PlotCamera = { cx: number; cy: number; scale: number };

export const MIN_SCALE = 1.5;
export const MAX_SCALE = 320;
export const DEFAULT_CAMERA: PlotCamera = { cx: 0, cy: 0, scale: 42 };

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return DEFAULT_CAMERA.scale;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function toScreen(camera: PlotCamera, size: PlotSize, point: PlotPoint): PlotPoint {
  return {
    x: size.width / 2 + (point.x - camera.cx) * camera.scale,
    y: size.height / 2 - (point.y - camera.cy) * camera.scale,
  };
}

export function toWorld(camera: PlotCamera, size: PlotSize, point: PlotPoint): PlotPoint {
  return {
    x: camera.cx + (point.x - size.width / 2) / camera.scale,
    y: camera.cy - (point.y - size.height / 2) / camera.scale,
  };
}

export type WorldBounds = { minX: number; maxX: number; minY: number; maxY: number };

export function worldBounds(camera: PlotCamera, size: PlotSize): WorldBounds {
  const halfWidth = size.width / 2 / camera.scale;
  const halfHeight = size.height / 2 / camera.scale;
  return {
    minX: camera.cx - halfWidth,
    maxX: camera.cx + halfWidth,
    minY: camera.cy - halfHeight,
    maxY: camera.cy + halfHeight,
  };
}

export function panPlot(camera: PlotCamera, dxPixels: number, dyPixels: number): PlotCamera {
  return {
    cx: camera.cx - dxPixels / camera.scale,
    cy: camera.cy + dyPixels / camera.scale,
    scale: camera.scale,
  };
}

/** Масштабирование вокруг точки экрана: мир под курсором остаётся на месте. */
export function zoomPlot(
  camera: PlotCamera,
  size: PlotSize,
  anchor: PlotPoint,
  factor: number,
): PlotCamera {
  const scale = clampScale(camera.scale * factor);
  const held = toWorld(camera, size, anchor);
  return {
    cx: held.x - (anchor.x - size.width / 2) / scale,
    cy: held.y + (anchor.y - size.height / 2) / scale,
    scale,
  };
}

/** Шаг сетки из ряда 1, 2, 5 × 10ⁿ — чтобы подписи делений были круглыми. */
export function gridStep(scale: number, targetPixels = 76): number {
  const raw = targetPixels / scale;
  const exponent = Math.floor(Math.log10(raw));
  const power = 10 ** exponent;
  const base = raw / power;
  const nice = base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10;
  return nice * power;
}

/** Деления шага `step`, попадающие в отрезок. Без накопления ошибки сложением. */
export function gridTicks(from: number, to: number, step: number): number[] {
  const first = Math.ceil(from / step);
  const last = Math.floor(to / step);
  const ticks: number[] = [];
  for (let index = first; index <= last && ticks.length < 400; index++) {
    ticks.push(index * step);
  }
  return ticks;
}

function paddingFor(size: PlotSize): number {
  return Math.max(28, Math.min(size.width, size.height) * 0.12);
}

/** Детерминированная подгонка под набор точек. */
export function fitPlot(points: PlotPoint[], size: PlotSize, padding?: number): PlotCamera {
  const usable = points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (usable.length === 0) return DEFAULT_CAMERA;

  const xs = usable.map((p) => p.x);
  const ys = usable.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const inset = padding ?? paddingFor(size);
  const availableWidth = Math.max(40, size.width - 2 * inset);
  const availableHeight = Math.max(40, size.height - 2 * inset);
  const spanX = maxX - minX;
  const spanY = maxY - minY;

  const candidates: number[] = [];
  if (spanX > 1e-9) candidates.push(availableWidth / spanX);
  if (spanY > 1e-9) candidates.push(availableHeight / spanY);
  const scale = clampScale(candidates.length === 0 ? DEFAULT_CAMERA.scale : Math.min(...candidates));

  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, scale };
}

/**
 * Доля полуэкрана, внутри которой важная точка считается спокойно видимой.
 * За ней камера расширяется.
 */
const SAFE = 0.86;
/** Один автоматический шаг масштаба. */
const MAX_STEP = 1.5;
/**
 * Приближение включается только когда подгонка выиграла бы больше, чем в
 * `ZOOM_IN_TRIGGER` раз. Между `1` и этим числом — мёртвая зона: она и есть
 * гистерезис, из-за которого плоскость не «дышит» на соседних значениях
 * коэффициента.
 */
const ZOOM_IN_TRIGGER = 2.2;
/**
 * Точка дальше этого числа полуэкранов в подгонке не участвует. Это правило
 * «далёкое пересечение не сплющивает график»: вместо нечитаемого масштаба
 * такая точка уходит в указатель у края.
 */
const FAR = 12;

export type AutoFitResult = { camera: PlotCamera; offscreen: PlotPoint[] };

export function isVisible(
  camera: PlotCamera,
  size: PlotSize,
  point: PlotPoint,
  margin = 0,
): boolean {
  const screen = toScreen(camera, size, point);
  return (
    screen.x >= -margin &&
    screen.x <= size.width + margin &&
    screen.y >= -margin &&
    screen.y <= size.height + margin
  );
}

/**
 * Мягкая автокоррекция после фиксации коэффициента.
 *
 * Расширяется охотно, приближается неохотно и не больше одного шага за раз.
 * Что не поместилось — возвращается отдельно: канва рисует такие точки
 * указателями у края, а не тянет камеру к ним.
 */
export function autoFitPlot(
  camera: PlotCamera,
  size: PlotSize,
  points: PlotPoint[],
  keepOrigin = true,
): AutoFitResult {
  const finite = points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const halfWidth = size.width / 2 / camera.scale;
  const halfHeight = size.height / 2 / camera.scale;
  const near = finite.filter(
    (p) =>
      Math.abs(p.x - camera.cx) <= FAR * halfWidth &&
      Math.abs(p.y - camera.cy) <= FAR * halfHeight,
  );
  const wanted = keepOrigin ? [...near, { x: 0, y: 0 }] : near;
  if (wanted.length === 0) return { camera, offscreen: finite };

  const outside = wanted.some((point) => {
    const screen = toScreen(camera, size, point);
    return (
      Math.abs(screen.x - size.width / 2) > (size.width / 2) * SAFE ||
      Math.abs(screen.y - size.height / 2) > (size.height / 2) * SAFE
    );
  });

  const target = fitPlot(wanted, size);
  let next = camera;
  if (outside) {
    next = { cx: target.cx, cy: target.cy, scale: clampScale(Math.max(target.scale, camera.scale / MAX_STEP)) };
  } else if (target.scale > camera.scale * ZOOM_IN_TRIGGER) {
    next = { cx: target.cx, cy: target.cy, scale: clampScale(Math.min(target.scale, camera.scale * MAX_STEP)) };
  }

  return { camera: next, offscreen: finite.filter((p) => !isVisible(next, size, p, -8)) };
}

/** Полная подгонка по кнопке «Показать всё» — без ограничения читаемости. */
export function showAll(points: PlotPoint[], size: PlotSize): PlotCamera {
  return fitPlot(points, size);
}

export type EdgeMark = { x: number; y: number; dx: number; dy: number };

/** Куда прижать указатель на точку, оставшуюся за кадром. */
export function edgeMark(camera: PlotCamera, size: PlotSize, point: PlotPoint): EdgeMark {
  const screen = toScreen(camera, size, point);
  const inset = 18;
  const x = Math.min(size.width - inset, Math.max(inset, screen.x));
  const y = Math.min(size.height - inset, Math.max(inset, screen.y));
  return {
    x,
    y,
    dx: screen.x > size.width - inset ? 1 : screen.x < inset ? -1 : 0,
    dy: screen.y > size.height - inset ? 1 : screen.y < inset ? -1 : 0,
  };
}
